import { useInstantSearch } from 'react-instantsearch';
import styled from 'styled-components';

import { Currency } from '@sorare/core/src/__generated__/globalTypes';
import { Caption, Text20 } from '@sorare/core/src/atoms/typography';
import { FilterTitle } from '@sorare/core/src/components/search/FilterTitle';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { CARD_LEVEL_MIN } from '@sorare/core/src/lib/cards';
import { FILTERS, Filter, FilterWidget } from '@sorare/core/src/lib/filters';
import { powerAlgorithm } from '@sorare/core/src/lib/slider';
import { toWei } from '@sorare/core/src/lib/wei';

import useFormatFilters from 'hooks/filters/useFormatFilters';

import RangeSlider from '../../search/RangeSlider';

interface TitleProps {
  filter: Filter;
  short?: boolean;
}

const Title = ({ filter, short }: TitleProps) => {
  const { indexUiState } = useInstantSearch();
  const selected = !!indexUiState.range?.[filter.attribute];

  return (
    <FilterTitle
      name={(short && filter?.shortTitle) || filter.title}
      selected={selected}
    />
  );
};

export const makeRangeFilter = (
  Component: React.FC<{ visible: boolean }>,
  filter: Filter,
  {
    startsOpen = false,
    hideTitle = false,
  }: {
    startsOpen?: boolean;
    hideTitle?: boolean;
  } = {}
): FilterWidget => ({
  key: filter.attribute,
  type: 'range',
  attribute: filter.attribute,
  Component: ({ visible }) => <Component visible={visible} />,
  title: !hideTitle && <Title filter={filter} />,
  shortTitle: !hideTitle && <Title filter={filter} short />,
  accordionOptions: { startsOpen },
});

export const RefineCardLevel = makeRangeFilter(({ visible }) => {
  const {
    so5: { cardMaxGrade },
  } = useConfigContext();
  return (
    <RangeSlider
      attribute={FILTERS.cardLevel.attribute}
      minForced={CARD_LEVEL_MIN}
      maxForced={cardMaxGrade}
      visible={visible}
    />
  );
}, FILTERS.cardLevel);

const refinePriceAlgorithm = powerAlgorithm({ power: 20 });

const REFINE_PRICE_MAX_WEI = toWei(100);

const StyledText20 = styled(Text20)`
  align-self: center;
`;
const StyledCaption = styled(Caption)`
  align-self: flex-end;
`;

const PriceRangeSlider = ({ visible }: { visible: boolean }) => {
  const { algoliaWeiFactor, sliderToPrice, priceToSlider } = useFormatFilters();
  const { currency, fiatCurrency } = useCurrentUserContext();

  return (
    <RangeSlider
      attribute={FILTERS.price.attribute}
      algorithm={refinePriceAlgorithm}
      fromInput={priceToSlider}
      toInput={sliderToPrice}
      maxForced={Number(REFINE_PRICE_MAX_WEI / algoliaWeiFactor)}
      greaterThanMode
      icon={
        currency === Currency.FIAT ? (
          <StyledText20 bold color="var(--c-white)">
            {fiatCurrency.symbol}
          </StyledText20>
        ) : (
          <StyledCaption bold color="var(--c-white)">
            ETH
          </StyledCaption>
        )
      }
      visible={visible}
    />
  );
};

export const RefinePrice = ({
  startsOpen = false,
}: {
  startsOpen?: boolean;
} = {}) =>
  makeRangeFilter(PriceRangeSlider, FILTERS.price, {
    startsOpen,
    hideTitle: true,
  });
